import { IconNames } from '@blueprintjs/icons';
import { BenefitType, BenefitTypeDisplayTextMap } from '@strata/constants';
import { formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ReferralChatIcon } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { PatientInfoList } from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppRoutes } from 'services/routes';
import { ReferralConversationCounts } from 'services/userSupportHelpers';
import { useAppSelector } from 'store';
import { useGetPagedReferralsQuery, WonReferralSummary } from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<WonReferralSummary>
>['sortProperties'] = [{ id: 'outreach_date', desc: false }];

const columns = ({
    referralConversationCounts,
}: {
    referralConversationCounts?: ReferralConversationCounts;
}): ColumnDef<WonReferralSummary>[] => [
    {
        accessorKey: 'product_name',
        header: 'Product',
        size: 190,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.is_brand} primary />
        ),
    },
    {
        accessorKey: 'last_name',
        header: 'Patient',
        size: 120,
        cell: ({ row, getValue }) => (
            <SecondaryData data={formatNameLastFirst(getValue(), row.original.first_name)} />
        ),
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'segment_name',
        header: 'Health Plan',
        size: 120,
    },
    {
        accessorKey: 'value_cases',
        header: 'Attributes',
        size: 80,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <PatientInfoList
                values={[
                    ...(getValue() ?? []),
                    ...(row.original.has_copay_assistance
                        ? [{ strat_rule_name: PatientInfoList.Types.COPAY }]
                        : []),
                ]}
                incentiveTotal={row.original.incentive_total}
                iconsOnly
            />
        ),
    },
    {
        accessorKey: 'referral_id',
        header: () => (
            <Icon
                icon={IconNames.CHAT}
                tooltipContent="Open conversations and unread messages indicator."
            />
        ),
        enableSorting: false,
        size: 40,
        cell: ({ getValue }) => (
            <ReferralChatIcon
                openConversations={referralConversationCounts?.[getValue()]?.open}
                unreadMessages={referralConversationCounts?.[getValue()]?.unread}
            />
        ),
    },
    {
        accessorKey: 'outreach_date',
        header: 'Outreach Start Date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'referral_start',
        header: 'Servicing Start Date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'referral_status',
        header: 'Outreach Status',
        size: 130,
        cell: ({ getValue }) => <ReferralStatusIndicator status={getValue()} />,
    },
];

export const UpcomingReferralsListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();

    const referralConversationCounts = useAppSelector(
        (state) => state.userSupport.referralConversationCounts,
    );

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.UPCOMING_REFERRALS, initialSortProperties);

    const apiQueryParams = {
        page,
        searchText: search,
        sort,
        upcoming: true,
        ...activeFilters,
    };

    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetPagedReferralsQuery({
        ...apiQueryParams,
        page: 0,
    });
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetPagedReferralsQuery(apiQueryParams);

    const { data: segmentsData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segmentsData ?? {};

    const filterCategories: FilterCategory<WonReferralSummary>[] = [
        {
            key: 'segment_name',
            title: 'Health Plan',
            customValues: firstPageData?.filterOptions?.segment_id?.map((value) => ({
                value,
                text: payerSegmentMap?.[value]?.name,
            })),
        },
        {
            key: 'product_name',
            title: 'Product',
            customValues: firstPageData?.filterOptions?.product_name?.map((value) => ({
                value,
            })),
        },
        {
            key: 'benefit',
            title: 'Benefit',
            customValues: firstPageData?.filterOptions?.benefit?.map((value) => ({
                value,
                text: BenefitTypeDisplayTextMap[value as BenefitType],
            })),
        },
        {
            key: 'referral_status',
            title: 'Outreach Status',
            customValues: firstPageData?.filterOptions?.referral_status?.map((value) => ({
                value,
                text: ReferralStatusIndicator.StatusMessages[
                    value as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses]
                ],
            })),
        },
    ];

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Upcoming Referrals"
                secondaryTitle={loggedInSupplierName}
                activeFilters={activeFilters}
                columns={columns({ referralConversationCounts })}
                currentPage={page}
                data={currentPageData?.referrals}
                enableSearch
                filterCategories={filterCategories}
                filterPaneRenderWidth={filterPaneWidth}
                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                manualSorting
                onFilter={updateGridActiveFilters}
                onFilterPaneResize={updateFilterPaneWidth}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) =>
                    navigate(AppRoutes.wonReferralDetails(rowData.referral_id))
                }
                onSearch={updateGridSearch}
                onSort={updateGridSort}
                rowCssClass={(rowData) =>
                    classNames({ 'griddle-row--disabled': dayjs().isBefore(rowData.outreach_date) })
                }
                searchText={search}
                sortProperties={sort}
                totalCount={firstPageData?.count}
            />
        </FormPageContainer>
    );
};
